/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardBody, Spinner, addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaCircleInfo,
  FaMapLocationDot,
  FaRegClock,
} from 'react-icons/fa6';
import { BsCheck2Circle } from 'react-icons/bs';
import fallbackImage from '../../../../public/fallback-image.png';
import {
  useCategoryQuery,
  useFetchServiceDataById,
} from '../../../hook/useQueryData';
import PlanSummaryCard from '../../../feature-module/components/common/ui/PlanSummaryCard';
import CustomButton from '../../components/CustomButton';
import { PackageTier, ServiceGet } from '../../../utils/type';
import { formatCurrency } from '../../../utils/currency';
import { all_routes } from '../../../core/data/routes/all_routes';
import { getAddOnId, getAddOnName } from '../../components/AddOnSelector';
import SessionAddOnSelector, {
  calculateSessionAddOnsSubtotal,
  getSessionAddOnValidationErrors,
  SessionAddOnSelection,
  toSessionAddOnsPayload,
} from '../../components/common/ui/Sessionaddonselector';
import CustomChip from '../../components/CustomChip';

const PackageSelection = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: categoriesData } = useCategoryQuery();
  const [selectedTier, setSelectedTier] = useState<PackageTier | null>(null);
  const [sessionAddOns, setSessionAddOns] = useState<SessionAddOnSelection>({});
  const [addOnErrors, setAddOnErrors] = useState<Record<string, string>>({});

  const { data, isLoading, isError } = useFetchServiceDataById(serviceId);
  const service: ServiceGet | null = useMemo(
    () =>
      data?.serviceInfo || data?.data?.serviceInfo || data?.serviceInfo || null,
    [data]
  );
  const tiers: PackageTier[] = service?.packageTiers || [];

  const lowestTierPrice = useMemo(() => {
    const prices = (service?.packageTiers || [])
      .map((tier) => Number(tier.price))
      .filter((n) => Number.isFinite(n) && n > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  }, [service]);

  const addOnCandidates = useMemo(
    () =>
      (service?.additionalServices || []).map((addon, index) => ({
        id: getAddOnId(addon, index),
        serviceItem: getAddOnName(addon, index),
        price: Number(addon.price) || 0,
        images: addon.images,
      })),
    [service?.additionalServices]
  );

  const addonSubtotal = useMemo(
    () => calculateSessionAddOnsSubtotal(addOnCandidates, sessionAddOns),
    [addOnCandidates, sessionAddOns]
  );

  const total = (selectedTier?.price || 0) + addonSubtotal;

  const handleTierSelect = (tier: PackageTier) => {
    setSelectedTier(tier);
    setSessionAddOns({});
    setAddOnErrors({});
  };

  const categoryName = useMemo(() => {
    const list =
      (categoriesData as any)?.categories ||
      (categoriesData as any)?.data?.categories ||
      (categoriesData as any)?.data ||
      [];

    if (!Array.isArray(list) || !service?.categoryId) return '';

    const match = list.find(
      (cat: any) =>
        cat?._id === service.categoryId ||
        cat?.categoryId === service.categoryId ||
        cat?.id === service.categoryId
    );

    return match?.categoryName || match?.name || match?.title || '';
  }, [categoriesData, service?.categoryId]);

  const handleContinueToCheckout = () => {
    if (!selectedTier) return;

    const errors = getSessionAddOnValidationErrors(
      addOnCandidates,
      sessionAddOns
    );
    if (Object.keys(errors).length > 0) {
      setAddOnErrors(errors);
      addToast({
        title: t('package.addon_validation_title', 'Selection required'),
        description: t(
          'package.addon_validation_desc',
          'Choose which session(s) each selected add-on applies to before continuing.'
        ),
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    const normalizedSessionAddOns = toSessionAddOnsPayload(sessionAddOns);
    const selectedAddOnsForCheckout = normalizedSessionAddOns.map((entry) => {
      const addon = addOnCandidates.find(
        (a) => a.id === entry.additionalServiceId
      );
      return {
        ...entry,
        serviceItem: addon?.serviceItem || '',
        price: addon?.price || 0,
      };
    });

    navigate(all_routes.servicePackageCheckout, {
      state: {
        serviceId: service?.serviceId,
        serviceTitle: service?.serviceTitle,
        tier: selectedTier,
        recurrence: selectedTier.recurrence,
        sessionAddOns: normalizedSessionAddOns,
        selectedAddOns: selectedAddOnsForCheckout,
        addonSubtotal,
      },
    });
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-2 sm:px-4 py-4">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 mb-3 cursor-pointer transition-colors"
      >
        <FaArrowLeft size={14} /> {t('package.back', 'Back')}
      </button>

      {/* Page Heading - moved above the grid so both columns align at the same level */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">
          {t('package.choose_title', 'Choose a Package')}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {service?.serviceTitle
            ? `${service.serviceTitle} — ${t(
                'package.choose_subtitle',
                'pick the plan that fits your needs.'
              )}`
            : t(
                'package.choose_subtitle',
                'pick the plan that fits your needs.'
              )}
        </p>
      </div>

      {/* Main 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_22rem] gap-8 items-start">
        {/* Left Column: Tiers, Add-ons, and Checkout */}
        <div className="space-y-8">
          {isLoading ? (
            <div className="flex justify-center py-24">
              <Spinner label="Loading packages…" />
            </div>
          ) : isError || !service ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="py-16 text-center text-slate-600">
                {t(
                  'package.load_error',
                  'We could not load packages for this service.'
                )}
              </CardBody>
            </Card>
          ) : tiers.length === 0 ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="py-16 text-center">
                <p className="text-base font-medium text-slate-700">
                  {t('package.empty_title', 'No packages available')}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {t(
                    'package.empty_hint',
                    'This service does not currently offer appointment packages.'
                  )}
                </p>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* Package Tiers Header & 3-Column Grid */}
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tiers.map((tier) => {
                    const isSelected = selectedTier
                      ? tier._id && selectedTier._id
                        ? tier._id === selectedTier._id
                        : tier.name === selectedTier.name
                      : false;

                    return (
                      <PlanSummaryCard
                        key={tier._id || tier.name}
                        tier={tier}
                        selected={isSelected}
                        buttonLabel={
                          isSelected
                            ? t('package.selected', 'Selected')
                            : t('package.select', 'Select Package')
                        }
                        onSelect={handleTierSelect}
                      />
                    );
                  })}
                </div>
              </div>

              {/* What's Included */}
              {service?.includes && service.includes.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-slate-800 mb-3">
                    {t('package.includes', "What's Included")}
                  </h2>
                  <div className="rounded-xl bg-slate-50 border border-slate-200/60 p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.includes.map((item, index) => (
                        <div key={index} className="flex items-center gap-2.5">
                          <BsCheck2Circle className="text-emerald-500 shrink-0 text-base" />
                          <p className="text-sm text-slate-700">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Services Area */}
              {addOnCandidates.length > 0 && (
                <div className="space-y-4">
                  {/* Information Banner */}
                  <div className="flex items-start gap-3 bg-blue-50/70 border border-blue-200/70 p-4 rounded-2xl text-blue-900">
                    <FaCircleInfo className="text-blue-500 shrink-0 mt-0.5 text-base" />
                    <div>
                      <h4 className="text-sm font-semibold">
                        {t(
                          'package.addon_banner_title',
                          'Customize Your Package Appointments'
                        )}
                      </h4>
                      <p className="text-xs text-blue-700 mt-0.5 leading-relaxed">
                        {t(
                          'package.addon_banner_desc',
                          'You can selectively add extras to individual appontments during package purchase or assign them later when booking specific time slots.'
                        )}
                      </p>
                    </div>
                  </div>

                  {/* ✅ Warning Banner - OUTSIDE disabled container, always visible and NOT blurred */}
                  {!selectedTier && (
                    <div className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                      <FaCircleInfo size={12} />
                      {t(
                        'package.select_tier_first_hint',
                        'Select a package tier above to unlock additional services.'
                      )}
                    </div>
                  )}

                  {/* Add-ons Section container with disabled dynamic state */}
                  <div
                    className={`transition-all duration-300 relative rounded-2xl ${
                      !selectedTier
                        ? 'opacity-50 pointer-events-none select-none filter blur-[0.2px]'
                        : ''
                    }`}
                  >
                    <h2 className="text-lg font-bold text-slate-800 mb-1">
                      {t('package.additional_services', 'Additional Services')}
                    </h2>
                    <p className="text-xs text-slate-500 mb-4">
                      {t(
                        'package.additional_services_hint',
                        'Optionally bundle add-ons into specific appointments of your package.'
                      )}
                    </p>

                    <SessionAddOnSelector
                      addons={addOnCandidates}
                      sessionCount={selectedTier?.sessionCount || 0}
                      value={sessionAddOns}
                      onChange={(next) => {
                        setSessionAddOns(next);
                        setAddOnErrors((prev) => {
                          const updated = { ...prev };
                          Object.keys(next).forEach((id) => {
                            if ((next[id] || []).length > 0) delete updated[id];
                          });
                          return updated;
                        });
                      }}
                      errors={addOnErrors}
                    />
                  </div>
                </div>
              )}

              {/* Checkout Bar - now static under additional services */}
              {selectedTier && (
                <Card
                  shadow="sm"
                  className="border border-slate-200 bg-white shadow-lg"
                >
                  <CardBody className="p-4 flex flex-row items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-xs text-slate-500">
                        {selectedTier.name} · {selectedTier.sessionCount}{' '}
                        {t('package.sessions_word', 'appointment(s)')}
                        {addonSubtotal > 0
                          ? ` + ${t('package.addons_word', 'add-ons')}`
                          : ''}
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(total)}
                      </p>
                    </div>
                    <CustomButton
                      label={t(
                        'package.continue_checkout',
                        'Continue to Checkout'
                      )}
                      color="primary"
                      size="md"
                      onPress={handleContinueToCheckout}
                    />
                  </CardBody>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Right Column: Service Summary Card - aligned to top */}
        <div className="space-y-3 lg:self-start">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            {t('package.service_summary', 'Service Details')}
          </p>

          <Card
            shadow="sm"
            className="border border-slate-200/80 rounded-2xl overflow-hidden bg-white shadow-sm"
          >
            {service ? (
              <>
                <div className="relative w-full aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img
                    src={
                      service?.gallery?.[0]?.serviceImages?.[0] || fallbackImage
                    }
                    alt={service?.serviceTitle || 'Service image'}
                    className="w-full h-full object-cover"
                  />
                </div>

                <CardBody className="p-4 space-y-3">
                  {categoryName && (
                    <div>
                      <CustomChip
                        label={categoryName}
                        size="sm"
                        variant="flat"
                        color="primary"
                        className="rounded-full text-xs"
                      />
                    </div>
                  )}

                  <div>
                    <h3 className="text-base font-bold text-slate-900 leading-snug">
                      {service?.serviceTitle || 'No title'}
                    </h3>
                    {(service?.businessName ||
                      service?.business?.name ||
                      service?.providerName) && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {service?.businessName ||
                          service?.business?.name ||
                          service?.providerName}
                      </p>
                    )}
                  </div>

                  {lowestTierPrice != null && (
                    <div className="bg-slate-50 border border-slate-100 p-2.5 rounded-xl flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        {t('package.starting_from', 'Starting From')}
                      </span>
                      <span className="text-sm font-bold text-slate-900">
                        {formatCurrency(lowestTierPrice)}
                      </span>
                    </div>
                  )}

                  {typeof service?.respondsWithinDays === 'number' && (
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <FaRegClock
                        className="text-blue-500 shrink-0"
                        size={13}
                      />
                      <span>
                        {t(
                          'quote.responds_within_days',
                          'Usually responds within {{count}} days',
                          { count: service.respondsWithinDays }
                        )}
                      </span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-600">
                    <FaMapLocationDot
                      className="text-blue-500 shrink-0 mt-0.5"
                      size={13}
                    />
                    <span className="line-clamp-2">
                      {[
                        service?.location?.[0]?.address,
                        service?.location?.[0]?.city,
                        service?.location?.[0]?.state,
                      ]
                        .filter(Boolean)
                        .join(', ') || 'Location N/A'}
                    </span>
                  </div>
                </CardBody>
              </>
            ) : (
              <CardBody className="p-6 text-center text-xs text-slate-400">
                Loading service info...
              </CardBody>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PackageSelection;
